import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { AddCertificates } from 'app/library/certificate/actions';
import { Certificate } from 'app/library/certificate/models/certificate.model';
import {
    AddCertificatesToModuleSuccess,
    AssignCertificateTemplateToModule,
    AssignCertificateTemplateToModuleFailure,
    FetchModuleCertificates,
    FetchParticipantModuleCertificates,
    FetchParticipantModuleCertificatesFailure,
    FetchParticipantModuleCertificatesSuccess,
} from 'app/library/module-certificates/actions';
import { UnsetModuleParticipants } from 'app/library/module-participants/actions';
import { UnsetModule } from 'app/library/module/actions';
import { AddModules } from 'app/library/module/actions/add-entities.action';
import { Module } from 'app/library/module/models/module.model';
import { ModuleStateModel } from 'app/library/module/models/module.state-model';
import { MaxBrainUtils } from 'app/projects/core/src/lib/utils';
import { EntityState } from 'app/projects/entity/src/lib/models/entity.state';
import {
    AssignCertificatesToModuleUsers,
    AssignCertificatesToModuleUsersFailure,
    AssignCertificatesToModuleUsersSuccess,
    CreatePrivateModuleCertificate,
    CreatePrivateModuleCertificateFailure,
    CreatePrivateModuleCertificateSuccess,
    CreatePublicModuleCertificate,
    CreatePublicModuleCertificateFailure,
    CreatePublicModuleCertificateSuccess,
    DeleteModuleCertificate,
    DeleteModuleCertificateSuccess,
    DeleteModuleCertificateTemplate,
    DeleteModuleCertificateTemplateSuccess,
    FetchModuleCertificate,
    FetchModuleCertificateFailure,
    FetchModuleCertificateSuccess,
    FetchModuleUsedTemplates,
    FetchModuleUsedTemplatesFailure,
    FetchModuleUsedTemplatesSuccess,
    SetModuleCertificate,
    UnassignCertificatesFromModuleUsers,
    UnassignCertificatesFromModuleUsersFailure,
    UnassignCertificatesFromModuleUsersSuccess,
    UnsetModuleCertificate,
    UpdateModuleCertificate,
    UpdateModuleCertificateFailure,
    UpdateModuleCertificateSuccess,
    UpdateModuleCertificateTemplate,
    UpdateModuleCertificateTemplateFailure,
    UpdateModuleCertificateTemplateSuccess,
} from '../actions';
import { AddModuleCertificates } from '../actions/add-entities.action';
import { ModuleCertificateApiService } from '../services/module-certificate.api-service';
import { ModuleCertificate } from './module-certificate.model';
import { ModuleCertificateStateModel } from './module-certificate.state-model';

@State<ModuleCertificateStateModel>({
    name: 'moduleCertificate',
    defaults: new ModuleCertificateStateModel(),
})
@Injectable()
export class ModuleCertificateState extends EntityState<ModuleCertificate> {
    static sortProperties: string[] = ['id'];

    @Selector()
    static getEntity(state: ModuleCertificateStateModel, id: string = state.item): ModuleCertificate {
        if (!state.map) {
            return null;
        }

        return state.map.get(id) || null;
    }

    @Selector()
    static getEntitiesAsMap(state: ModuleCertificateStateModel): Map<string, ModuleCertificate> {
        if (!state.map) {
            return null;
        }
        return state.map;
    }

    @Selector()
    static getEntities(state: ModuleCertificateStateModel): ModuleCertificate[] {
        if (!state.map) {
            return null;
        }

        const toReturn = Array.from(state.map.values());

        toReturn.sort(MaxBrainUtils.sortCompareFn(ModuleCertificateState.sortProperties));

        return toReturn;
    }

    constructor(private moduleCertificateApiService: ModuleCertificateApiService) {
        super(null, 0);
    }

    @Action(SetModuleCertificate)
    setEntity(ctx: StateContext<ModuleCertificateStateModel>, action: SetModuleCertificate): void {
        this._setEntity(ctx, action, AddModuleCertificates);
    }

    @Action(UnsetModule)
    @Action(UnsetModuleCertificate)
    unsetEntity(ctx: StateContext<ModuleCertificateStateModel>): void {
        this._unsetEntity(ctx);

        ctx.dispatch(new UnsetModuleParticipants());
    }

    @Action(AddModuleCertificates)
    addEntities(ctx: StateContext<ModuleCertificateStateModel>, action: AddModuleCertificates): void {
        const certificates: Certificate[] = [];
        const modules: Module[] = [];

        action.payload = action.payload.map((certificateUserExtended) => {
            if (certificateUserExtended.certificate) {
                if (certificates.map((user) => user.id).indexOf(certificateUserExtended.certificate.id) === -1) {
                    certificates.push(certificateUserExtended.certificate);
                }

                delete certificateUserExtended.certificate;
            }

            if (certificateUserExtended.module) {
                if (modules.map((module) => module.id).indexOf(certificateUserExtended.module.id) === -1) {
                    modules.push(certificateUserExtended.module);
                }

                delete certificateUserExtended.module;
            }

            return certificateUserExtended;
        });

        ctx.dispatch(new AddCertificates(certificates));
        ctx.dispatch(new AddModules(modules));

        this._addEntities(ctx, action);
    }

    @Action(CreatePrivateModuleCertificate)
    async createPrivateModuleCertificate(ctx: StateContext<ModuleCertificateStateModel>, { payload }: CreatePrivateModuleCertificate): Promise<void> {
        try {
            const entity = await this.moduleCertificateApiService.assignPrivate(payload).toPromise();

            this.setExpirationTime(entity.id);

            ctx.dispatch([new AddModuleCertificates([entity]), new CreatePrivateModuleCertificateSuccess(entity)]);
        } catch (e) {
            if (e instanceof HttpErrorResponse) {
                ctx.dispatch(new CreatePrivateModuleCertificateFailure({ response: e }));
            } else {
                throw new Error(e);
            }
        }
    }

    @Action(CreatePublicModuleCertificate)
    async createPublicModuleCertificate(ctx: StateContext<ModuleCertificateStateModel>, { payload }: CreatePublicModuleCertificate): Promise<void> {
        try {
            const entity = await this.moduleCertificateApiService.assignPublic(payload).toPromise();

            this.setExpirationTime(entity.id);

            ctx.dispatch([new AddModuleCertificates([entity]), new CreatePublicModuleCertificateSuccess(entity)]);
        } catch (e) {
            if (e instanceof HttpErrorResponse) {
                ctx.dispatch(new CreatePublicModuleCertificateFailure({ response: e }));
            } else {
                throw new Error(e);
            }
        }
    }

    @Action(UpdateModuleCertificate)
    updateEntity(ctx: StateContext<ModuleCertificateStateModel>, action: UpdateModuleCertificate): void {
        this._updateEntity(
            ctx,
            action,
            UpdateModuleCertificateSuccess,
            UpdateModuleCertificateFailure,
            AddModuleCertificates,
            this.moduleCertificateApiService.update(action.payload)
        );
    }

    @Action(FetchModuleCertificate)
    fetchEntity(ctx: StateContext<ModuleCertificateStateModel>, action: FetchModuleCertificate): void {
        this._fetchEntity(
            ctx,
            action,
            FetchModuleCertificateSuccess,
            FetchModuleCertificateFailure,
            AddModuleCertificates,
            SetModuleCertificate,
            this.moduleCertificateApiService.getByModuleId(action.payload)
        );
    }

    @Action(FetchModuleCertificateSuccess)
    fetchEntitySuccess({ getState, patchState }: StateContext<ModuleCertificateStateModel>, { payload }: FetchModuleCertificateSuccess): void {
        const state = getState();

        patchState({
            fetchingItem: state.fetchingItem.filter((entityId) => entityId !== payload.moduleId),
        });
    }

    @Action(FetchModuleCertificateFailure)
    fetchEntityFailure({ getState, patchState }: StateContext<ModuleCertificateStateModel>, { payload }: FetchModuleCertificateFailure): void {
        const state = getState();

        patchState({
            fetchingItem: state.fetchingItem.filter((entityId) => entityId !== payload.entityId),
        });
    }

    @Action(DeleteModuleCertificateTemplate)
    deleteModuleCertificateTemplate(ctx: StateContext<ModuleCertificateStateModel>, action: DeleteModuleCertificateTemplate): void {
        this.moduleCertificateApiService.deleteModuleCertificate(action.payload.certificateId).subscribe(() => {
            ctx.dispatch([new FetchModuleCertificates({ moduleId: action.payload.moduleId }), new DeleteModuleCertificateTemplateSuccess()]);
            const state = ctx.getState();

            const filteredModuleCertificateList = Array.from(state.map.values()).filter((moduleUser) => moduleUser.certificateId === action.payload.certificateId);

            this._removeEntitiesFromState(
                ctx,
                filteredModuleCertificateList.map((moduleUser) => moduleUser.id)
            );
        });
    }

    @Action(DeleteModuleCertificate)
    deleteModuleCertificate(ctx: StateContext<ModuleCertificateStateModel>, action: DeleteModuleCertificate): void {
        this.moduleCertificateApiService.deleteCertificate(action.payload).subscribe(() => {
            ctx.dispatch(new DeleteModuleCertificateSuccess());
            const state = ctx.getState();
            let filteredModuleCertificateList = [];

            if (state.map) {
                filteredModuleCertificateList = Array.from(state.map.values()).filter((moduleUser) => moduleUser.certificateId === action.payload);
            }

            this._removeEntitiesFromState(
                ctx,
                filteredModuleCertificateList.map((moduleUser) => moduleUser.id)
            );
        });
    }

    @Action(UpdateModuleCertificateTemplate)
    updateModuleCertificateTemplate({ dispatch }: StateContext<ModuleCertificateStateModel>, action: UpdateModuleCertificateTemplate): void {
        this.moduleCertificateApiService.updateModuleCertificateTemplate(action.payload.id, action.payload.certificateName).subscribe(
            (certificate: Certificate) => {
                dispatch([new FetchModuleCertificates({ moduleId: action.payload.moduleId }), new UpdateModuleCertificateTemplateSuccess(certificate)]);
            },
            (response: HttpErrorResponse) => {
                dispatch(new UpdateModuleCertificateTemplateFailure());
            }
        );
    }

    @Action(AssignCertificateTemplateToModule)
    assignAttendanceTemplateToModule({ dispatch }: StateContext<ModuleStateModel>, { payload }: AssignCertificateTemplateToModule): void {
        this.moduleCertificateApiService
            .assignCertificateTemplateToModule(payload.moduleId, payload.certificateName, payload.file, payload.assignmentRule, payload.sectionId)
            .subscribe(
                (certificates) => {
                    dispatch([new AddModuleCertificates(Array(certificates)), new AddCertificatesToModuleSuccess(Array(certificates))]);
                },
                (response: HttpErrorResponse) => {
                    dispatch(new AssignCertificateTemplateToModuleFailure());
                }
            );
    }

    @Action(AssignCertificatesToModuleUsers)
    assignCertificatesToModuleUsers({ dispatch, getState }: StateContext<ModuleStateModel>, { payload }: AssignCertificatesToModuleUsers): void {
        this.moduleCertificateApiService.assignCertificatesToModuleUsers(payload).subscribe(
            (entities) => {
                dispatch(new AssignCertificatesToModuleUsersSuccess(entities));
            },
            (response: HttpErrorResponse) => {
                dispatch(new AssignCertificatesToModuleUsersFailure({ response }));
            }
        );
    }

    @Action(UnassignCertificatesFromModuleUsers)
    unassignCertificatesFromModuleUsers({ dispatch, getState }: StateContext<ModuleStateModel>, { payload }: UnassignCertificatesFromModuleUsers): void {
        this.moduleCertificateApiService.unassignCertificatesFromModuleUsers(payload).subscribe(
            (numberOfremovedParticipants) => {
                dispatch(new UnassignCertificatesFromModuleUsersSuccess(numberOfremovedParticipants));
            },
            (response: HttpErrorResponse) => {
                dispatch(new UnassignCertificatesFromModuleUsersFailure({ response }));
            }
        );
    }

    @Action(FetchParticipantModuleCertificates)
    fetchParticipantModuleCertificates({ dispatch }: StateContext<ModuleStateModel>, { payload }: FetchParticipantModuleCertificates): void {
        this.moduleCertificateApiService.fetchParticipantModuleCertificates(payload).subscribe(
            (entities) => {
                dispatch(new FetchParticipantModuleCertificatesSuccess(entities));
            },
            (error: HttpErrorResponse) => {
                dispatch(new FetchParticipantModuleCertificatesFailure({ response: error }));
            },
            () => {
                this.setExpirationTime(payload);
            }
        );
    }

    @Action(FetchModuleUsedTemplates)
    fetchAllUsedTemplates({ dispatch }: StateContext<ModuleCertificateStateModel>, { payload }: FetchModuleUsedTemplates): void {
        this.moduleCertificateApiService.fetchModuleUsedTemplates(payload.moduleId).subscribe(
            (entities) => {
                dispatch(new FetchModuleUsedTemplatesSuccess(entities));
            },
            (error: HttpErrorResponse) => {
                dispatch(new FetchModuleUsedTemplatesFailure({ response: error }));
            }
        );
    }
}
